import { useCallback, useEffect, useRef, useState } from 'react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import appPageCopy from '@/i18n/ns/app-page';
import appSourcePlanCopy from '@/i18n/ns/app-source-plan';
import { useChatInterfaceLocale } from '@/lib/chat-i18n';
import { SessionChangedError } from '@/lib/session-guard';
import { Button } from '@/components/ui/button';
import { InlineError, LoadingState } from '../../primitives';
import type { AppSourcesApi, DeclaredRefreshJob, SourcesRead } from './app-sources-api';
import { SourcePreview } from './source-preview';
import { useSourceCommit } from './use-source-commit';

/** The supported refresh grammar: one run a day, "M H * * *". Anything else is not shown as a time. */
const DAILY = /^(\d{1,2}) (\d{1,2}) \* \* \*$/u;
export const dailyExpression = (time: string): string | null => {
  const match = /^(\d{2}):(\d{2})$/u.exec(time);
  if (!match) return null;
  const [hour, minute] = [Number(match[1]), Number(match[2])];
  return hour < 24 && minute < 60 ? `${minute} ${hour} * * *` : null;
};
export const dailyTime = (expression: string): string | null => {
  const match = DAILY.exec(expression);
  if (!match) return null;
  const [minute, hour] = [Number(match[1]), Number(match[2])];
  return hour < 24 && minute < 60
    ? `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    : null;
};

const zones = (current: string): string[] => {
  const all =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  return !current || all.includes(current) ? all : [current, ...all];
};

/**
 * The selected App's sources and refresh time, for editors: what the saved version reads, renewing that access
 * ("Renew access") and the daily refresh time. Both are changes to the App, so each goes through the existing
 * save chain and opens the new saved version. No tool picker exists yet, so nothing here adds a new source.
 */
export function AppSourcesSection({
  api,
}: {
  api: Pick<
    AppSourcesApi,
    'read' | 'prepareSource' | 'prepareRefresh' | 'completeRefresh' | 'refreshResult' | 'preview'
  >;
}) {
  const copy = useNamespaceCopy(appSourcePlanCopy);
  const page = useNamespaceCopy(appPageCopy);
  const locale = useChatInterfaceLocale();
  const commit = useSourceCommit();
  const [read, setRead] = useState<SourcesRead | null>(null);
  const [failed, setFailed] = useState(false);
  const alive = useRef(true);
  const load = useCallback(async () => {
    try {
      const next = await api.read();
      if (!alive.current) return;
      setRead(next);
      setFailed(false);
    } catch (error) {
      if (error instanceof SessionChangedError) return;
      if (alive.current) setFailed(true);
    }
  }, [api]);
  useEffect(() => {
    alive.current = true;
    void load();
    return () => {
      alive.current = false;
    };
  }, [load]);
  if (!read)
    return failed ? (
      <InlineError message={page.portionUnavailable} onRetry={() => void load()} />
    ) : (
      <LoadingState />
    );
  const busy = commit.phase === 'working';
  const rollout = { complete: api.completeRefresh, result: api.refreshResult };
  const nameOf = (sourceId: string | null) =>
    read.sources.find((source) => source.id === sourceId)?.name ?? null;
  return (
    <section className="pc-app-sources" data-testid="app-sources">
      <h3>{copy.heading}</h3>
      {read.sources.length === 0 && read.tools.length > 0 ? (
        // The tools name sources the list did not return: never "reads no sources".
        <InlineError message={page.portionUnavailable} onRetry={() => void load()} />
      ) : read.sources.length === 0 ? (
        <p className="pc-tracker-note">{copy.none}</p>
      ) : (
        <ul>
          {read.sources.map((source) => {
            const tool = read.tools.find((entry) => entry.sourceId === source.id && entry.toolRef);
            return (
              <li key={source.id} data-testid="app-source">
                <strong>{source.name}</strong>
                {tool && <SourcePreview api={api} sourceId={source.id} toolId={tool.id} />}
                {tool && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    data-testid="app-source-renew"
                    disabled={busy}
                    onClick={() =>
                      void commit.run((operationId) =>
                        api.prepareSource({
                          operationId,
                          locale,
                          sourceId: source.id,
                          sourceName: source.name,
                          toolId: tool.id,
                          toolName: tool.name,
                          toolRef: tool.toolRef as string,
                          argumentTemplate: tool.argumentTemplate,
                          intent: 'renew',
                        }),
                      )
                    }
                  >
                    {copy.renew}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {read.jobs
        .filter((job) => job.sourceId !== null)
        .map((job) => (
          <RefreshRow
            key={job.id}
            job={job}
            sourceName={nameOf(job.sourceId)}
            busy={busy}
            onSave={(schedule) =>
              void commit.run(
                (operationId) =>
                  api.prepareRefresh({
                    operationId,
                    locale,
                    sourceId: job.sourceId as string,
                    schedule,
                  }),
                rollout,
              )
            }
          />
        ))}
      {read.sources
        .filter(
          (source) =>
            read.tools.some((tool) => tool.sourceId === source.id && tool.toolRef) &&
            !read.jobs.some((job) => job.sourceId === source.id),
        )
        .map((source) => (
          <RefreshRow
            key={`first-${source.id}`}
            job={null}
            sourceName={source.name}
            busy={busy}
            onSave={(schedule) =>
              void commit.run(
                (operationId) =>
                  api.prepareRefresh({ operationId, locale, sourceId: source.id, schedule }),
                rollout,
              )
            }
          />
        ))}
      <p role="status" data-testid="app-sources-status">
        {commit.phase === 'working'
          ? copy.working
          : commit.phase === 'saved'
            ? copy.saved
            : commit.phase === 'failed'
              ? copy.failed
              : commit.phase === 'unconfirmed' || commit.phase === 'completion-unknown'
                ? copy.unconfirmed
                : ''}
      </p>
      {(commit.phase === 'unconfirmed' || commit.phase === 'completion-unknown') && (
        <Button type="button" variant="outline" size="sm" onClick={() => void commit.checkAgain()}>
          {copy.checkAgain}
        </Button>
      )}
    </section>
  );
}

function RefreshRow({
  job,
  sourceName,
  busy,
  onSave,
}: {
  /** The emitted refresh job, or null for a declared source that has no refresh plan yet (the first one). */
  job: DeclaredRefreshJob | null;
  sourceName: string | null;
  busy: boolean;
  onSave: (schedule: { expression: string; timeZone: string }) => void;
}) {
  const copy = useNamespaceCopy(appSourcePlanCopy);
  const current = job ? dailyTime(job.schedule.expression) : null;
  const [time, setTime] = useState(current ?? '');
  // A first plan has no zone to start from: the person chooses one (never a browser or UTC guess).
  const [zone, setZone] = useState(job?.schedule.timeZone ?? '');
  const [invalid, setInvalid] = useState(false);
  return (
    <form
      className="pc-app-refresh"
      data-testid="app-refresh"
      onSubmit={(event) => {
        event.preventDefault();
        const expression = dailyExpression(time);
        if (!expression || !zone) return setInvalid(true);
        setInvalid(false);
        if (!busy) onSave({ expression, timeZone: zone });
      }}
    >
      <h4>{sourceName ? `${copy.refreshHeading} · ${sourceName}` : copy.refreshHeading}</h4>
      {current && (
        <p className="pc-tracker-note" data-testid="app-refresh-now">
          {formatCopy(copy.refreshDaily, { time: current, zone: job?.schedule.timeZone ?? '' })}
        </p>
      )}
      <label>
        {copy.timeLabel}
        <input
          type="time"
          data-testid="app-refresh-time"
          value={time}
          onChange={(event) => setTime(event.target.value)}
          onInput={(event) => setTime(event.currentTarget.value)}
        />
      </label>
      <label>
        {copy.zoneLabel}
        <select
          data-testid="app-refresh-zone"
          value={zone}
          onChange={(event) => setZone(event.target.value)}
        >
          {!job && (
            <option value="" disabled>
              {' '}
            </option>
          )}
          {zones(job?.schedule.timeZone ?? '').map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </select>
      </label>
      <Button type="submit" size="sm" data-testid="app-refresh-save" disabled={busy}>
        {copy.refreshSave}
      </Button>
      {invalid && <p role="alert">{copy.invalidTime}</p>}
    </form>
  );
}
